import * as feedApi from '@apis/app-feed';
import { ReactComponent as ChevronRight } from '@assets/images/app/chevron-right-nutral70.svg';
import { ReactComponent as DefaultAvatar } from '@assets/images/app/default-avatar-gray.svg';
import { Carousel } from '@components/common/Carousel';
import { DeepLinkActions, useDeeplink } from '@hooks/deeplink';
import { ProductModel } from '@models/product';
import { UserContent, UserContentContainer } from '@models/user-contents';
import { useQuery } from '@tanstack/react-query';
import { cdnImageUrl } from '@utils/image';
import React from 'react';
import UserContentSummary from '../lounge/UserContentSummary';

export const PartnerReviewSummary: React.FC<{ data: ProductModel; reviewInfo: UserContentContainer }> = (props) => {
  const { data, reviewInfo } = props;
  const { linkTo } = useDeeplink();

  const loungeCurationCategoriesQuery = useQuery({
    queryKey: [`lounge-curation-categories`],
    queryFn: () => feedApi.getLoungeCurationCategories(),
  });

  const loungeCurationCategories = loungeCurationCategoriesQuery.data?.data?.data || [];
  const loungeCurationCategoriesDict: { [key: string]: string | undefined } = {};
  loungeCurationCategories.forEach((item) => {
    loungeCurationCategoriesDict[item.type] = item.title;
  });

  const productId = data._id;
  const reviews = reviewInfo.contents || [];
  const totalCount = reviewInfo.total || 0;

  return (
    <div
      style={{
        padding: '16px',
        margin: '12px 0',
        marginBottom: 0,
        backgroundColor: '#ffffff',
      }}>
      <div>
        <button
          className="link-button"
          style={{ height: '32px', display: 'flex', alignItems: 'center' }}
          onClick={() => {
            linkTo({ action: DeepLinkActions.GO_TO_PARTNER, payload: { partnerId: data.sellerId } });
          }}>
          {data.sellerProfileImage ? (
            <img
              src={cdnImageUrl(data.sellerProfileImage)}
              alt="partner"
              style={{ width: '32px', height: '32px', display: 'block', objectFit: 'cover', borderRadius: '16px' }}
            />
          ) : (
            <DefaultAvatar
              style={{ width: '32px', height: '32px', display: 'block', objectFit: 'cover', borderRadius: '16px' }}
            />
          )}
          <div
            style={{
              color: 'var(--Neutral-100, #242424)',
              fontFamily: 'Pretendard',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 700,
              marginLeft: '12px',
            }}>
            {data.sellerName}
          </div>
          <ChevronRight />
        </button>
        {data.sellerIntroduction && (
          <div
            style={{
              marginTop: '12px',
              color: 'var(--Neutral-90, #424242)',
              fontFamily: 'Pretendard',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '24px',
              display: '-webkit-box',
              maxWidth: '100%',
              maxHeight: '72px',
              WebkitLineClamp: '3',
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-all',
              whiteSpace: 'pre-wrap',
            }}>
            {data.sellerIntroduction}
          </div>
        )}
      </div>
      {reviews.length > 0 && <div style={{ background: '#EFF1F4', margin: '20px 0', height: '1px', width: '100%' }} />}
      {reviews.length > 0 && (
        <div>
          <div
            style={{
              color: 'var(--Neutral-100, #242424)',
              fontFamily: 'Pretendard',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 700,
              marginBottom: '16px',
            }}>
            <span>찐후기</span>
            {totalCount > 0 && <span style={{ color: '#FF3D8F', marginLeft: '4px' }}>{totalCount}</span>}
          </div>
          <div>
            <Carousel
              options={{
                dragFree: true,
                align: 'start',
              }}>
              {reviews.map((item: UserContent, index: number) => {
                return (
                  <div
                    key={`review-carousel-item-${index}`}
                    className={'embla__slide'}
                    style={{
                      flex: `0 0 280px`,
                      border: '1px solid #e8eaed',
                      borderRadius: '12px',
                      marginRight: '8px',
                    }}>
                    <div
                      style={{
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        height: '100%',
                      }}>
                      <div
                        onClick={() => {
                          linkTo({ action: DeepLinkActions.GO_TO_LOUNGE, payload: { userContentId: item._id } });
                        }}
                        style={{ marginBottom: '16px', cursor: 'pointer' }}>
                        <div
                          style={{
                            fontSize: '15px',
                            color: '#424242',
                            lineHeight: '24px',
                            maxHeight: '120px',
                            letterSpacing: -0.4,
                            overflow: 'hidden',
                          }}>
                          <UserContentSummary
                            item={item}
                            hideName
                            imageSize="60px"
                            imagePosition="left"
                            loungeCurationCategoriesDict={loungeCurationCategoriesDict}></UserContentSummary>
                        </div>
                      </div>
                      <div
                        onClick={() => {
                          if (item.product?._id !== productId) {
                            linkTo(
                              {
                                action: DeepLinkActions.GO_TO_PRODUCT,
                                payload: { productId: item.product?._id },
                              },
                              { source: 'seller-review-summary', productId: productId },
                            );
                          }
                        }}
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          width: '100%',
                          borderTop: '1px solid #eff1f4',
                          paddingTop: '12px',
                          marginTop: 'auto',
                        }}>
                        <div
                          style={{
                            width: '246px',
                            wordBreak: 'break-word',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            color: 'var(--Neutral-90, #424242)',
                            textOverflow: 'ellipsis',
                            fontFamily: 'Pretendard',
                            fontSize: '13px',
                            fontStyle: 'normal',
                            fontWeight: 400,
                          }}>
                          {item.product?.title}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </Carousel>
          </div>
        </div>
      )}
    </div>
  );
};
